import React, { useEffect, useMemo, useRef } from 'react';
import { Container, Sprite } from '@pixi/react';
import { observer } from 'mobx-react-lite';

import { jackPotStore } from '../../../store/jackPotStore';

import { Spine } from '../../../core/classes/spine';
import { isMobile } from '../../../core/helpers/isMobile';
import { getGameImages } from '../../../core/helpers/getGameImages';

import JackPotWinPopup from './jackPotWinPopup';
import JackpotBackGround from './jackpotBackground';
import JackPotSceneItemWrapper from './jackPotSceneItem';
import JackPotSceneNumberItemWrapper from './jackPotNumberItem';
import ScaleComponent from '../../mobile/scaleComponent';
import FullScreenComponent from '../../mobile/fullScreenComponent';
import ExpireDate from '../expireDate';

const jackPotSymbols = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const jackpotIcons = {
  1: 'clubs',
  2: 'diamond',
  3: 'heart',
  4: 'spades',
};

const JackpotScene = observer(() => {
  const jackpotGameSceneBoardRef = useRef();
  const jackpotBackgroundRef = useRef();
  const {
    jackpotBg,
    jackPotHeartSymbolCollectStep0,
    jackPotSpadeSymbolCollectStep0,
    jackPotClubsSymbolCollectStep0,
    jackPotDiamondSymbolCollectStep0,
  } = getGameImages();
  const { isShowJackPotScene, jackPotData } = jackPotStore;
  const symbolMoveContainerRef = useRef(null);

  const jackPotItemEnum = useMemo(
    () => [
      {
        id: 3,
        x: isMobile() ? 110 : 170,
        y: isMobile() ? 132 : 180,
        isWin: false,
        type: 'heartAmount',
        name: 'JackPotHeartSectionContainer',
        image: jackPotHeartSymbolCollectStep0,
        positions: isMobile()
          ? [
              { x: 85, y: 127, scale: 0.3 },
              { x: 135, y: 127, scale: 0.3 },
              { x: 110, y: 127, scale: 0.4 },
            ]
          : [
              { x: 113, y: 177 },
              { x: 227, y: 177 },
              { x: 170, y: 177 },
            ],
      },
      {
        id: 4,
        x: isMobile() ? 428 : 1263,
        y: isMobile() ? 132 : 180,
        isWin: false,
        type: 'spadeAmount',
        name: 'JackPotSpadeSectionContainer',
        image: jackPotSpadeSymbolCollectStep0,
        positions: isMobile()
          ? [
              { x: 402, y: 127, scale: 0.3 },
              { x: 455, y: 127, scale: 0.3 },
              { x: 429, y: 131, scale: 0.4 },
            ]
          : [
              { x: 1207, y: 171 },
              { x: 1317, y: 171 },
              { x: 1263, y: 171 },
            ],
      },
      {
        id: 1,
        x: isMobile() ? 157 : 281,
        y: isMobile() ? 230 : 374,
        isWin: false,
        type: 'clubAmount',
        name: 'JackPotClubsSectionContainer',
        image: jackPotClubsSymbolCollectStep0,
        positions: isMobile()
          ? [
              { x: 130, y: 227, scale: 0.3 },
              { x: 185, y: 227, scale: 0.3 },
              { x: 157, y: 227, scale: 0.4 },
            ]
          : [
              { x: 224, y: 369 },
              { x: 338, y: 369 },
              { x: 280, y: 369 },
            ],
      },
      {
        id: 2,
        x: isMobile() ? 381 : 1136,
        y: isMobile() ? 230 : 374,
        isWin: false,
        type: 'diamondAmount',
        name: 'JackPotDiamondSectionContainer',
        image: jackPotDiamondSymbolCollectStep0,
        positions: isMobile()
          ? [
              { x: 355, y: 227, scale: 0.3 },
              { x: 406, y: 227, scale: 0.3 },
              { x: 381, y: 227, scale: 0.4 },
            ]
          : [
              { x: 1082, y: 370 },
              { x: 1190, y: 370 },
              { x: 1136, y: 370 },
            ],
      },
    ],
    [
      jackPotClubsSymbolCollectStep0,
      jackPotDiamondSymbolCollectStep0,
      jackPotHeartSymbolCollectStep0,
      jackPotSpadeSymbolCollectStep0,
    ]
  );

  const winIcon = useMemo(() => {
    return jackPotData.find((el) => el.isWin);
  }, [jackPotData]);

  useEffect(() => {
    if (!jackpotGameSceneBoardRef.current || !isShowJackPotScene) return;
    const jackpotAmountBoardSpine = new Spine({ key: 'jackpotGameSceneBoard' });
    jackpotAmountBoardSpine.play('idle', true);
    jackpotGameSceneBoardRef.current.addChild(jackpotAmountBoardSpine);
  }, [jackpotGameSceneBoardRef, isShowJackPotScene]);

  useEffect(() => {
    if (winIcon) {
      jackpotGameSceneBoardRef.current?.children[0].play(
        `win_${jackpotIcons[winIcon.jackpotCard]}`,
        true
      );
    }
  }, [winIcon]);

  if (!isShowJackPotScene) {
    return null;
  }

  return (
    <Container name='JackPotSceneContainer'>
      <Container
        x={isMobile() ? 0 : 720}
        y={isMobile() ? 0 : 410}
        ref={jackpotBackgroundRef}
        name='JackpotGameSceneBoardContainer'
      >
        {isMobile() ? (
          <>
            <FullScreenComponent>
              <Sprite
                texture={jackpotBg}
                anchor={0.5}
                width={window.innerWidth}
                height={window.innerHeight}
              />
            </FullScreenComponent>
            <JackpotBackGround />
          </>
        ) : (
          <>
            <Sprite texture={jackpotBg} anchor={0.5} />
            <Container ref={jackpotGameSceneBoardRef} />
          </>
        )}
      </Container>
      <ScaleComponent isCenterHorizontal>
        <Container name='JackpotGameSceneIconsContainer'>
          <JackPotSceneNumberItemWrapper winIcon={winIcon} jackPotItemEnum={jackPotItemEnum} />
          <JackPotSceneItemWrapper
            jackpotIcons={jackpotIcons}
            jackPotSymbols={jackPotSymbols}
            jackPotItemEnum={jackPotItemEnum}
            symbolMoveContainerRef={symbolMoveContainerRef}
          />
          <Container ref={symbolMoveContainerRef} name='JackPotSymbolMoveContainer' />
          <ExpireDate x={isMobile() ? 268 : 715} y={isMobile() ? 110 : 140} />
          {winIcon && <JackPotWinPopup winIcon={winIcon} jackpotIcons={jackpotIcons} />}
        </Container>
      </ScaleComponent>
    </Container>
  );
});

export default JackpotScene;
